import { useCallback, useMemo, type ChangeEvent } from 'react';
import { Slider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BlendIcon } from '@/UI/Icons';
import { useBlendView, useNodeNullable } from '@/components/Recipe/FlowContext';
import { Input } from '@/UI/Input/Input';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCenHorVer } from '@/UI/styles';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { BLEND_MODES } from '@/components/Recipe/FlowComponents/Editor/Designer/BlendModes';
import { SelectInput } from '@/components/Recipe/FlowComponents/Editor/Designer/LayerPropertyPanel/SelectInput';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { NodeViewer } from '../Shared/FileViewer';
import type { BlendMode, NodeId } from 'web';
import type { BlendData } from '@/types/node';

interface BlendNodeProps {
  id: NodeId;
  data: BlendData;
}

export function BlendNode({ id, data }: BlendNodeProps) {
  const role = useUserWorkflowRole();
  const view = useBlendView(id);
  const { t } = useTranslation();

  const locked = useNodeNullable(id)?.locked ?? true;
  const editable = !locked && role === 'editor';

  const blendModeOptions = useMemo(() => {
    return BLEND_MODES.map((mode) => ({
      id: mode.value,
      label: t(mode.label),
      value: mode.value,
    }));
  }, [t]);

  const opacity = data.options.opacity * 100;
  const blendMode = data.options.blendMode;

  const handleBlendModeChange = useCallback(
    (mode: BlendMode, ongoing: boolean) => {
      view.setBlendMode(mode, ongoing);
    },
    [view],
  );

  const handleOpacitySliderChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      const value = (Array.isArray(newValue) ? newValue[0] : newValue) / 100;
      view.setOpacity(value, true);
    },
    [view],
  );

  const handleOpacitySliderChangeFinal = useCallback(
    (_, newValue: number | number[]) => {
      const value = (Array.isArray(newValue) ? newValue[0] : newValue) / 100;
      view.setOpacity(value, false);
    },
    [view],
  );

  const handleOpacityInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value) / 100;
      view.setOpacity(value, false);
    },
    [view],
  );

  const handleReset = useCallback(() => view.cancelOngoingAction(), [view]);

  return (
    <DynamicNode2 id={id} data={data} icon={<BlendIcon />}>
      <NodeViewer id={id} />
      <FlexCenVer className="nodrag" sx={{ mt: 2, justifyContent: 'space-between', gap: 4 }}>
        <FlexCenVer sx={{ gap: 1, justifyContent: 'space-between' }}>
          <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
            Mode
          </Typography>
          <SelectInput
            options={blendModeOptions}
            value={blendMode}
            onSubmit={handleBlendModeChange}
            onReset={handleReset}
            disabled={!editable}
          />
        </FlexCenVer>

        <FlexCenVer sx={{ gap: 1 }}>
          <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
            {t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.OPACITY)}
          </Typography>
          <FlexCenHorVer sx={{ width: '120px' }}>
            <Slider
              size="small"
              value={opacity}
              onChange={handleOpacitySliderChange}
              onChangeCommitted={handleOpacitySliderChangeFinal}
              min={0}
              max={100}
              step={1}
              disabled={!editable}
              slotProps={{
                root: {
                  style: {
                    padding: '8px 0',
                  },
                },
              }}
            />
          </FlexCenHorVer>
          <Input
            type="number"
            size="small"
            value={Math.round(opacity)}
            onChange={handleOpacityInputChange}
            disabled={!editable}
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ width: 60 }}
          />
        </FlexCenVer>
      </FlexCenVer>
    </DynamicNode2>
  );
}
